import type { ChecklistSourceUpload } from './checklist-source';

/** Exact current upload-envelope acceptance, shared by ordinary uploads and P2a. */
export function parseChecklistSourceUpload(
  input: unknown,
): { ok: true; upload: ChecklistSourceUpload } | { ok: false } {
  try {
    const value = input as Partial<ChecklistSourceUpload>;
    const schemaVersion = Number(value.schemaVersion);
    const product = value.product as Record<string, unknown> | undefined;
    const productValid =
      product !== undefined &&
      product !== null &&
      product['sourceSystem'] === 'azure-sql-ebay' &&
      ['sourceTargetId', 'category', 'year', 'brand', 'product'].every(
        (field) => typeof product[field] === 'string',
      );
    const rowsValid =
      Array.isArray(value.rows) &&
      value.rows.every((row) => {
        if (row === null || typeof row !== 'object') return false;
        const candidate = row as unknown as Record<string, unknown>;
        if (
          !['sourceTargetId', 'sourceItemId', 'sourceSubsetId', 'subset'].every(
            (field) => typeof candidate[field] === 'string',
          ) ||
          !['number', 'name'].every(
            (field) =>
              typeof candidate[field] === 'string' ||
              candidate[field] === undefined ||
              candidate[field] === null,
          )
        ) {
          return false;
        }
        return (
          (candidate['team'] === undefined || typeof candidate['team'] === 'string') &&
          (candidate['sourceFile'] === undefined || typeof candidate['sourceFile'] === 'string') &&
          (candidate['autographAuthentication'] === undefined ||
            typeof candidate['autographAuthentication'] === 'string') &&
          (candidate['autographAuthenticationNumber'] === undefined ||
            typeof candidate['autographAuthenticationNumber'] === 'string') &&
          (candidate['autographFormat'] === undefined ||
            typeof candidate['autographFormat'] === 'string') &&
          candidate['category'] === undefined &&
          candidate['printRun'] === undefined &&
          stringMapIsValid(candidate['marketplaceAspects']) &&
          [
            'parallelName',
            'parallelNameTitleSafe',
            'cardType',
            'subsetTitleSafe',
            'nameTitleSafe',
            'notes',
            'shortPrintNote',
            'sourceFamilyId',
            'rarity',
            'finish',
            'illustrator',
            'featuredPerson',
            'eventTournament',
            'ccgAttributesRaw',
          ].every(
            (field) => candidate[field] === undefined || typeof candidate[field] === 'string',
          ) &&
          (candidate['ccgAttributes'] === undefined ||
            (candidate['ccgAttributes'] !== null &&
              typeof candidate['ccgAttributes'] === 'object' &&
              !Array.isArray(candidate['ccgAttributes']))) &&
          (candidate['serialTo'] === undefined ||
            (Number.isInteger(candidate['serialTo']) &&
              Number(candidate['serialTo']) >= 1 &&
              Number(candidate['serialTo']) <= 1_000_000)) &&
          (candidate['players'] === undefined ||
            (Array.isArray(candidate['players']) &&
              candidate['players'].every((player) => typeof player === 'string'))) &&
          ['rookie', 'shortPrint', 'autograph', 'memorabilia'].every(
            (field) => candidate[field] === undefined || typeof candidate[field] === 'boolean',
          )
        );
      });
    const parallelDefinitionsValid =
      Array.isArray(value.parallelDefinitions) &&
      value.parallelDefinitions.every((definition) => {
        if (definition === null || typeof definition !== 'object') return false;
        const candidate = definition as unknown as Record<string, unknown>;
        const applicability = candidate['applicability'] as Record<string, unknown> | undefined;
        return (
          ['sourceTargetId', 'sourceSubsetId', 'name'].every(
            (field) => typeof candidate[field] === 'string',
          ) &&
          typeof candidate['autograph'] === 'boolean' &&
          typeof candidate['memorabilia'] === 'boolean' &&
          typeof candidate['shortPrint'] === 'boolean' &&
          (candidate['sourceFamilyId'] === undefined ||
            typeof candidate['sourceFamilyId'] === 'string') &&
          (candidate['nameTitleSafe'] === undefined ||
            typeof candidate['nameTitleSafe'] === 'string') &&
          ['confirmed', 'source-claimed', 'unknown'].includes(
            String(candidate['applicabilityConfidence']),
          ) &&
          applicability !== undefined &&
          applicability !== null &&
          typeof applicability === 'object' &&
          ['all-cards-in-subset', 'explicit-cards', 'unknown'].includes(
            String(applicability['kind']),
          )
        );
      });
    const subsetDefinitionsValid =
      Array.isArray(value.subsetDefinitions) &&
      value.subsetDefinitions.every((definition) => {
        if (definition === null || typeof definition !== 'object') return false;
        const candidate = definition as unknown as Record<string, unknown>;
        return (
          ['sourceTargetId', 'sourceSubsetId', 'name'].every(
            (field) => typeof candidate[field] === 'string',
          ) &&
          typeof candidate['autograph'] === 'boolean' &&
          typeof candidate['memorabilia'] === 'boolean' &&
          typeof candidate['shortPrint'] === 'boolean' &&
          ['confirmed', 'source-claimed', 'unknown'].includes(
            String(candidate['applicabilityConfidence']),
          ) &&
          (candidate['noPlainVersion'] === undefined ||
            typeof candidate['noPlainVersion'] === 'boolean') &&
          candidate['applicability'] === undefined &&
          (candidate['serialTo'] === undefined ||
            (Number.isInteger(candidate['serialTo']) &&
              Number(candidate['serialTo']) >= 1 &&
              Number(candidate['serialTo']) <= 1_000_000)) &&
          ['printRunText', 'sourceFile', 'notes', 'sourceFamilyId'].every(
            (field) => candidate[field] === undefined || typeof candidate[field] === 'string',
          ) &&
          (candidate['nameTitleSafe'] === undefined ||
            typeof candidate['nameTitleSafe'] === 'string')
        );
      });
    if (
      schemaVersion !== 7 ||
      typeof value.extractedAt !== 'string' ||
      !Number.isFinite(Date.parse(value.extractedAt)) ||
      (value.sourceHighWater !== undefined &&
        (typeof value.sourceHighWater !== 'string' ||
          !Number.isFinite(Date.parse(value.sourceHighWater)))) ||
      !productValid ||
      (product?.['brandTitleSafe'] !== undefined &&
        typeof product['brandTitleSafe'] !== 'string') ||
      (product?.['productTitleSafe'] !== undefined &&
        typeof product['productTitleSafe'] !== 'string') ||
      !stringMapIsValid(product?.['marketplaceAspects']) ||
      (product?.['franchise'] !== undefined && typeof product['franchise'] !== 'string') ||
      (product?.['tvShow'] !== undefined && typeof product['tvShow'] !== 'string') ||
      (product?.['game'] !== undefined && typeof product['game'] !== 'string') ||
      (product?.['movie'] !== undefined && typeof product['movie'] !== 'string') ||
      (product?.['genre'] !== undefined && typeof product['genre'] !== 'string') ||
      (product?.['manufacturer'] !== undefined && typeof product['manufacturer'] !== 'string') ||
      (product?.['categoryType'] !== undefined &&
        product?.['categoryType'] !== 'non_sport' &&
        product?.['categoryType'] !== 'sport' &&
        product?.['categoryType'] !== 'ccg') ||
      !rowsValid ||
      !parallelDefinitionsValid ||
      !subsetDefinitionsValid ||
      !Number.isInteger(value.expectedRosterCount) ||
      Number(value.expectedRosterCount) < 0 ||
      !Number.isInteger(value.expectedParallelDefinitionCount) ||
      Number(value.expectedParallelDefinitionCount) < 0 ||
      !Number.isInteger(value.expectedSubsetDefinitionCount) ||
      Number(value.expectedSubsetDefinitionCount) < 0
    ) {
      return { ok: false };
    }
    return {
      ok: true,
      upload: {
        ...value,
        rows: (value.rows ?? []).map((row) => ({
          ...row,
          number: row.number ?? '',
          name: row.name ?? '',
        })),
      } as ChecklistSourceUpload,
    };
  } catch {
    return { ok: false };
  }
}

function stringMapIsValid(value: unknown): boolean {
  return (
    value === undefined ||
    (value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.entries(value as Record<string, unknown>).every(
        ([key, entry]) => key.trim() !== '' && typeof entry === 'string',
      ))
  );
}
